import { Button, Input } from 'antd';
import { Form, Formik } from 'formik';
import { useEffect, useState } from 'react';
import * as Yup from 'yup';

import Loader from '../Loader';
import FileUploader from '../utils/imageUploader';

import useCreateNewsLetter from '@/hooks/newsLetter/useCreateNewsLetter';
import useUpdateNewsLetter from '@/hooks/newsLetter/useEditNewsLetter';
import { INewsLetter, INewsLetterDto } from '@/interfaces/entities/newsLetter';
import { notificationService } from '@/services/notification.service';

type PropType = { editNews: INewsLetter | null; setToggle: () => void };
export const CreateNewsLetter = ({ editNews, setToggle }: PropType) => {
	const [loading, setLoading] = useState<boolean>(false);
	const createNews = useCreateNewsLetter();
	const updateNews = useUpdateNewsLetter();

	const [initialValues, setInitialValues] = useState<INewsLetterDto>({
		title: '',
		heading: '',
		description1: '',
		description2: '',
		location: '',
		img: null,
		pdf: null,
	});

	const handleSubmit = async (
		values: INewsLetterDto,
		{ resetForm }: { resetForm: () => void },
	) => {
		setLoading(true);
		if (!editNews) {
			const formData = new FormData();
			formData.append('title', values.title);
			formData.append('heading', values.heading);
			formData.append('description1', values.description1);
			formData.append('description2', values.description2);
			formData.append('location', values.location);

			// Handle image file
			if (values.img && values.img instanceof File) {
				formData.append('img', values.img); // actual file
			} else if (typeof values.img === 'string') {
				formData.append('img_url', values.img); // just the URL
			}
			if (values.pdf && values.pdf instanceof File) {
				formData.append('pdf', values.pdf); // actual file
			} else if (typeof values.pdf === 'string') {
				formData.append('pdf_url', values.pdf); // just the URL
			}

			try {
				await createNews.mutateAsync(formData);
				resetForm();
				setToggle();
			} catch (error) {
				notificationService.error('NewsLetter Created failed');
			}
            finally{
                setLoading(false);
            }
		} else {
			const formData = new FormData();
			formData.append('title', values.title);
			formData.append('heading', values.heading);
			formData.append('description1', values.description1);
			formData.append('description2', values.description2);
			formData.append('location', values.location);

			// Handle image file
			if (values.img && values.img instanceof File) {
				formData.append('img', values.img); // actual file
			} else if (typeof values.img === 'string') {
				formData.append('img_url', values.img); // just the URL
			}
			if (values.pdf && values.pdf instanceof File) {
				formData.append('pdf', values.pdf); // actual file
			} else if (typeof values.pdf === 'string') {
				formData.append('pdf_url', values.pdf); // just the URL
			}

			try {
				await updateNews.mutateAsync([editNews.id, formData]);
				resetForm();
				setToggle();
			} catch (error) {
				notificationService.error('newsLetter Updated failed');
			}
            finally{
                setLoading(false);
            }
		}
	};

	const handleCancel = () => {
		setToggle();
		setInitialValues({
			title: '',
			heading: '',
			description1: '',
			description2: '',
			location: '',
			img: null,
			pdf: null,
		});
	};

	const blogValidationSchema = Yup.object().shape({
		title: Yup.string().required('Title is required'),
		heading: Yup.string().required('Heading is required'),
		description1: Yup.string().required('Description is required'),
		description2: Yup.string().required('Description is required'),
		location: Yup.string().required('Location is required'),
		img: Yup.mixed().nullable().required('Image is required'),
		pdf: Yup.mixed().nullable().required('pdf is required'),
	});

	useEffect(() => {
		if (editNews) {
			setInitialValues({
				title: editNews.title,
				heading: editNews.heading,
				description1: editNews.description1,
				description2: editNews.description2,
				location: editNews.location,
				pdf: editNews?.pdf || null,
				img: editNews?.img || null,
			});
		}
	}, [editNews]);

	return (
		<div className="w-[100%] h-[100%]">
			{loading && (
				<div className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm bg-black/30 flex-col gap-5">
					<Loader />
					<p className='text-[20px] font-semibold'>Processing...</p>
				</div>
			)}

			<Formik
				initialValues={initialValues}
				onSubmit={handleSubmit}
				validationSchema={blogValidationSchema}
				enableReinitialize={true}
			>
				{({
					handleChange,
					handleBlur,
					values,
					errors,
					touched,
					setFieldValue: _setFieldValue,
				}) => (
					<Form>
						<div className="flex justify-between gap-5 mb-5">
							{/* Title Input */}
							<div className="w-1/2 flex flex-col gap-3">
								<label className="text-[20px] font-semibold">Title</label>
								<Input
									name="title"
									value={values.title}
									onChange={handleChange}
									onBlur={handleBlur}
									placeholder="Enter title"
									className="py-[10px] h-[50px] text-[18px]"
								/>
								{touched.title && errors.title && (
									<div className="text-red-500">{errors.title}</div>
								)}
							</div>

							{/* Heading Input */}
							<div className="w-1/2 flex flex-col gap-3">
								<label className="text-[20px] font-semibold">Heading</label>
								<Input
									name="heading"
									value={values.heading}
									onChange={handleChange}
									onBlur={handleBlur}
									placeholder="Enter heading"
									className="py-[10px] h-[50px] text-[18px]"
								/>
								{touched.heading && errors.heading && (
									<div className="text-red-500">{errors.heading}</div>
								)}
							</div>
						</div>
						{/* Description Input */}
						<div className="w-full flex flex-col gap-3">
							<label className="text-[20px] font-semibold">Description1</label>
							<Input
								name="description1"
								value={values.description1}
								onChange={handleChange}
								onBlur={handleBlur}
								placeholder="Enter description1"
								className="py-[10px] h-[50px] text-[18px]"
							/>
							{touched.description1 && errors.description1 && (
								<div className="text-red-500">{errors.description1}</div>
							)}
						</div>

						{/* Description Input */}
						<div className="flex flex-col gap-3 mt-5">
							<label className="text-[20px] font-semibold">Description2</label>
							<Input.TextArea
								name="description2"
								value={values.description2}
								onChange={handleChange}
								onBlur={handleBlur}
								placeholder="Enter description"
								rows={4}
								className="py-[10px] h-[50px] text-[18px]"
							/>
							{touched.description2 && errors.description2 && (
								<div className="text-red-500">{errors.description2}</div>
							)}
						</div>
						{/* Description Input */}
						<div className="w-full flex flex-col gap-3 mt-5">
							<label className="text-[20px] font-semibold">Location</label>
							<Input
								name="location"
								value={values.location}
								onChange={handleChange}
								onBlur={handleBlur}
								placeholder="Enter location"
								className="py-[10px] h-[50px] text-[18px]"
							/>
							{touched.location && errors.location && (
								<div className="text-red-500">{errors.location}</div>
							)}
						</div>

						{/* Editor */}

						{/* Image Upload */}
						<div className="flex flex-col gap-3 mt-5">
							<label className="text-[20px] font-semibold">
								Cover Image & Pdf
							</label>
							<FileUploader
								mode="image-and-pdf"
								imageField="img"
								pdfField="pdf"
							/>
						</div>
						{touched.img && errors.img && (
							<div className="text-red-500">{errors.img}</div>
						)}
						{touched.pdf && errors.pdf && (
							<div className="text-red-500">{errors.pdf}</div>
						)}

						{/* Submit Button */}
						<div className="mt-5 w-full flex justify-end pb-10 gap-5">
							<Button
								className="text-[16px] font-semibold"
								htmlType="button"
								onClick={handleCancel}
							>
								Cancel
							</Button>
							<Button
								color="default"
								variant="solid"
								className="text-[16px] font-semibold"
								htmlType="submit"
							>
								Submit
							</Button>
						</div>
					</Form>
				)}
			</Formik>
		</div>
	);
};
